import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  AccountTree as FlowIcon,
  Code as MermaidIcon,
  ViewModule as TemplateIcon,
  Share as ShareIcon,
  Save as SaveIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';

import FlowCanvas from '../components/canvas/FlowCanvas';
import PageContainer from '../components/common/PageContainer';

// Predefined templates
const flowTemplates = [
  {
    id: 'user-registration',
    title: 'User Registration Flow',
    description: 'Complete user registration process with email verification',
    category: 'Authentication',
    mermaidCode: `flowchart TD
    A((Start)) --> B[User enters details]
    B --> C{Valid email?}
    C -->|No| D[Show error message]
    D --> B
    C -->|Yes| E[Send verification email]
    E --> F[User clicks verification link]
    F --> G{Link valid?}
    G -->|No| H[Show error page]
    G -->|Yes| I[Activate account]
    I --> J[Send welcome email]
    J --> K((End))`
  },
  {
    id: 'api-workflow',
    title: 'API Request Workflow',
    description: 'Standard API request handling with authentication and validation',
    category: 'API Design',
    mermaidCode: `flowchart TD
    A((Start)) --> B[Receive API Request]
    B --> C{Valid Auth Token?}
    C -->|No| D[Return 401 Unauthorized]
    C -->|Yes| E{Valid Request Format?}
    E -->|No| F[Return 400 Bad Request]
    E -->|Yes| G[Process Request]
    G --> H{Success?}
    H -->|No| I[Return 500 Server Error]
    H -->|Yes| J[Return 200 Success]
    D --> K((End))
    F --> K
    I --> K
    J --> K`
  },
  {
    id: 'payment-process',
    title: 'Payment Processing Flow',
    description: 'Secure payment processing with multiple payment methods',
    category: 'E-commerce',
    mermaidCode: `flowchart TD
    A((Start)) --> B[Select Payment Method]
    B --> C{Credit Card?}
    C -->|Yes| D[Validate Card Details]
    C -->|No| E{PayPal?}
    E -->|Yes| F[Redirect to PayPal]
    E -->|No| G[Bank Transfer]
    D --> H{Card Valid?}
    H -->|No| I[Show Error]
    I --> B
    H -->|Yes| J[Process Payment]
    F --> J
    G --> J
    J --> K{Payment Success?}
    K -->|No| L[Handle Payment Failure]
    K -->|Yes| M[Send Confirmation]
    L --> N((End))
    M --> N`
  },
  {
    id: 'data-pipeline',
    title: 'Data Processing Pipeline',
    description: 'ETL data processing workflow with validation and error handling',
    category: 'Data Engineering',
    mermaidCode: `flowchart TD
    A((Start)) --> B[Extract Data]
    B --> C{Data Available?}
    C -->|No| D[Log No Data]
    C -->|Yes| E[Validate Data Format]
    E --> F{Format Valid?}
    F -->|No| G[Log Format Error]
    F -->|Yes| H[Transform Data]
    H --> I[Apply Business Rules]
    I --> J{Validation Passed?}
    J -->|No| K[Log Validation Error]
    J -->|Yes| L[Load to Database]
    L --> M{Load Success?}
    M -->|No| N[Log Load Error]
    M -->|Yes| O[Update Metrics]
    D --> P((End))
    G --> P
    K --> P
    N --> P
    O --> P`
  },
  {
    id: 'deployment-pipeline',
    title: 'CI/CD Deployment Pipeline',
    description: 'Continuous integration and deployment workflow',
    category: 'DevOps',
    mermaidCode: `flowchart TD
    A((Start)) --> B[Code Commit]
    B --> C[Trigger Build]
    C --> D[Run Unit Tests]
    D --> E{Tests Pass?}
    E -->|No| F[Notify Developer]
    E -->|Yes| G[Build Application]
    G --> H{Build Success?}
    H -->|No| I[Build Failed]
    H -->|Yes| J[Run Integration Tests]
    J --> K{Tests Pass?}
    K -->|No| L[Rollback]
    K -->|Yes| M[Deploy to Staging]
    M --> N[Run E2E Tests]
    N --> O{Tests Pass?}
    O -->|No| P[Staging Failed]
    O -->|Yes| Q[Deploy to Production]
    Q --> R[Health Check]
    R --> S{Healthy?}
    S -->|No| T[Auto Rollback]
    S -->|Yes| U[Deployment Success]
    F --> V((End))
    I --> V
    L --> V
    P --> V
    T --> V
    U --> V`
  }
];

const FlowDesigner = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  // Redirect to new Flow Builder
  useEffect(() => {
    navigate(`/projects/${projectId}/flow-builder`);
  }, [projectId, navigate]);

  const [currentTab, setCurrentTab] = useState(0);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [saveDialog, setSaveDialog] = useState(false);
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleTemplateSelect = (template) => {
    // This would load the template into the canvas
    console.log('Loading template:', template);
    setTemplateDialog(false);
    // TODO: Implement template loading
  };

  const handleSaveFlow = () => {
    // TODO: Implement flow saving
    console.log('Saving flow:', { flowName, flowDescription });
    setSaveDialog(false);
  };

  return (
    <PageContainer>
      <Container maxWidth={false} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            underline="hover" 
            color="inherit" 
            onClick={() => navigate('/dashboard')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Dashboard
          </Link>
          <Link 
            underline="hover" 
            color="inherit" 
            onClick={() => navigate(`/projects/${projectId}`)}
            sx={{ cursor: 'pointer' }}
          >
            Project
          </Link>
          <Typography color="text.primary">Flow Designer</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlowIcon color="primary" />
            Flow Designer
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Create interactive flowcharts and process diagrams for your project
          </Typography>
        </Box>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                icon={<FlowIcon />} 
                label="Flow Canvas" 
                iconPosition="start"
              />
              <Tab 
                icon={<TemplateIcon />} 
                label="Templates" 
                iconPosition="start"
              />
            </Tabs>

            {/* Canvas Tab */}
            {currentTab === 0 && (
              <Box sx={{ height: 'calc(100% - 48px)', position: 'relative' }}>
                <FlowCanvas />
                
                {/* Floating Action Buttons */}
                <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1, zIndex: 1000 }}>
                  <Button
                    variant="contained"
                    startIcon={<TemplateIcon />}
                    onClick={() => setTemplateDialog(true)}
                    size="small"
                  >
                    Templates
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => setSaveDialog(true)}
                    size="small"
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            )}

            {/* Templates Tab */}
            {currentTab === 1 && (
              <Box sx={{ p: 3, height: 'calc(100% - 48px)', overflow: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                  Flow Templates
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Choose from pre-built templates to quickly create common workflow patterns
                </Typography>

                <Grid container spacing={3}>
                  {flowTemplates.map((template) => (
                    <Grid item xs={12} sm={6} md={4} key={template.id}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <FlowIcon color="primary" />
                            <Typography variant="h6" component="h3">
                              {template.title}
                            </Typography>
                          </Box>
                          <Chip 
                            label={template.category} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ mb: 2 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {template.description}
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button 
                            size="small" 
                            startIcon={<MermaidIcon />}
                            onClick={() => handleTemplateSelect(template)}
                          >
                            Use Template
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Template Selection Dialog */}
        <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Load Template</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Loading a template will replace your current flowchart. Are you sure you want to continue?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTemplateDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => setTemplateDialog(false)}>
              Load Template
            </Button>
          </DialogActions>
        </Dialog>

        {/* Save Flow Dialog */}
        <Dialog open={saveDialog} onClose={() => setSaveDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Save Flowchart</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Flow Name"
              fullWidth
              variant="outlined"
              value={flowName}
              onChange={(e) => setFlowName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={flowDescription}
              onChange={(e) => setFlowDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialog(false)}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleSaveFlow}
              disabled={!flowName.trim()}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PageContainer>
  );
};

export default FlowDesigner;